/**
 * Legacy-side `postMessage` listener that filters `surface:mutation`
 * events coming from a Next embed body. Used by entity pages (e.g.,
 * `IssueProfileTabs`, `PostCommentsTabs`) to invalidate comment counts
 * and inline comment queries after a reply lands inside the drawer.
 *
 * Keeps the `window.addEventListener` bookkeeping in one place so every
 * entity page consumes the same same-origin / schema-guarded contract.
 *
 * See [frontend-next/src/types/surfaces.ts](../../../frontend-next/src/types/surfaces.ts)
 * and [docs/architecture/adr/011-cra-next-surface-bridge.md](../../../docs/architecture/adr/011-cra-next-surface-bridge.md).
 */

import { useEffect } from "react";

import {
  isSurfaceEvent,
  type SurfaceEvent,
} from "types/surfaces";

interface MutationPayload {
  entityType: string;
  entityId: string | number;
  kind: "create" | "update" | "delete";
  childEntityType?: string;
  childEntityId?: string | number;
}

export interface UseSurfaceMutationListenerOptions {
  /**
   * Primary entity this listener cares about. Incoming events whose
   * `entityType`/`entityId` don't match are ignored.
   */
  entityType: string;
  entityId: string | number | null | undefined;
  /**
   * Callback invoked once per matching event. Typically wraps
   * `queryClient.invalidateQueries(...)` — KEEP IT STABLE (use
   * `useCallback`) so the effect's dependency array is honest.
   */
  onMutation: (payload: MutationPayload) => void;
  /**
   * Skip attaching the listener when falsy. Allows conditional use
   * (e.g., only on narrow viewports where the drawer is enabled).
   */
  enabled?: boolean;
}

/**
 * Narrow an inbound message event to a mutation payload matching the
 * configured entity. Returns `null` if it's any other event or targets
 * a different entity.
 */
const extractMatchingMutation = (
  event: MessageEvent,
  entityType: string,
  entityId: string | number
): MutationPayload | null => {
  if (event.origin !== window.location.origin) {
    return null;
  }
  if (!isSurfaceEvent(event.data)) {
    return null;
  }
  const data: SurfaceEvent = event.data;
  if (data.type !== "surface:mutation") {
    return null;
  }
  if (data.entityType !== entityType) {
    return null;
  }
  if (String(data.entityId) !== String(entityId)) {
    return null;
  }
  return {
    entityType: data.entityType,
    entityId: data.entityId,
    kind: data.kind,
    childEntityType: data.childEntityType,
    childEntityId: data.childEntityId,
  };
};

export const useSurfaceMutationListener = ({
  entityType,
  entityId,
  onMutation,
  enabled = true,
}: UseSurfaceMutationListenerOptions): void => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (entityId === null || entityId === undefined) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }

    const handler = (event: MessageEvent): void => {
      const payload = extractMatchingMutation(event, entityType, entityId);
      if (!payload) {
        return;
      }
      onMutation(payload);
    };

    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, [enabled, entityId, entityType, onMutation]);
};
